import type { Card } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import type { DeckEntry } from '@mtg-deck-tool/shared';

// ── Card geometry ─────────────────────────────────────────────────────────────

/**
 * Base card size in px at 100% zoom. The single source of truth: the rendered
 * size (the --pt-card-w/h CSS variables), the battlefield grid defaults and the
 * hand-fan overlap maths must all derive from these, or layout silently
 * disagrees with what is on screen.
 */
export const CARD_BASE_W = 128;
export const CARD_BASE_H = 180;

/** Card dimensions in px at a given zoom percentage. */
export function cardDimensions(zoom: number): { cardW: number; cardH: number } {
  return {
    cardW: Math.round(CARD_BASE_W * zoom / 100),
    cardH: Math.round(CARD_BASE_H * zoom / 100),
  };
}

// ── Instance creation ─────────────────────────────────────────────────────────

/**
 * Generate a new CardInstance from a Card.
 * Produces a fresh UUID for instanceId and sets all defaults.
 * @param card - The source card.
 * @returns A new CardInstance with default state.
 */
export function createInstance(card: Card, isCommander = false, isToken = false): CardInstance {
  return {
    instanceId: crypto.randomUUID(),
    card,
    tapped: false,
    faceDown: false,
    doesntUntap: false,
    counters: {},
    castCount: 0,
    isCommander,
    isToken,
  };
}

// ── Shuffle ───────────────────────────────────────────────────────────────────

/**
 * Fisher-Yates shuffle — returns a new array without mutating the input.
 * @param instances - The array of CardInstances to shuffle.
 * @returns A new shuffled array.
 */
export function shuffleInstances(instances: CardInstance[]): CardInstance[] {
  const result = [...instances];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = result[i]!;
    result[i] = result[j]!;
    result[j] = temp;
  }
  return result;
}

// ── Zone building ─────────────────────────────────────────────────────────────

/**
 * Build the initial zone distribution from a full deck entry list.
 * Commanders → command zone (castCount: 0).
 * Remaining cards → shuffled library.
 * Returns { command, library } — caller draws 7 to hand.
 * @param entries - The full deck entry list.
 * @returns { command: CardInstance[], library: CardInstance[] }
 */
export function buildInitialZones(entries: DeckEntry[]): {
  command: CardInstance[];
  library: CardInstance[];
} {
  const command: CardInstance[] = [];
  const libraryInstances: CardInstance[] = [];

  for (const entry of entries) {
    for (let i = 0; i < entry.quantity; i++) {
      const instance = createInstance(entry.card, entry.isCommander);
      if (entry.isCommander) {
        command.push(instance);
      } else {
        libraryInstances.push(instance);
      }
    }
  }

  return {
    command,
    library: shuffleInstances(libraryInstances),
  };
}

// ── Draw ──────────────────────────────────────────────────────────────────────

/**
 * Draw N cards from the top of the library.
 * Returns new hand and library arrays — does not mutate inputs.
 * If n > library.length, draws min(n, library.length).
 * @param library - Current library array (top is index 0).
 * @param hand - Current hand array.
 * @param n - Number of cards to draw.
 * @returns { hand: CardInstance[], library: CardInstance[] }
 */
export function drawFromLibrary(
  library: CardInstance[],
  hand: CardInstance[],
  n: number,
): { hand: CardInstance[]; library: CardInstance[] } {
  const count = Math.min(n, library.length);
  const drawn = library.slice(0, count);
  const remaining = library.slice(count);
  return {
    hand: [...hand, ...drawn],
    library: remaining,
  };
}

// ── Horde reveal ──────────────────────────────────────────────────────────────

/**
 * Horde reveal: flip from top of library until a non-token is found.
 * A card is a token if its Card.layout === 'token'.
 * Returns the revealed batch and the remaining library.
 * If the entire library is tokens, returns all cards as revealed and empty remaining.
 * If the library is empty, returns empty revealed and empty remaining.
 * @param library - Current horde library array (top is index 0).
 * @returns { revealed: CardInstance[], remaining: CardInstance[] }
 */
export function hordeReveal(library: CardInstance[]): {
  revealed: CardInstance[];
  remaining: CardInstance[];
} {
  if (library.length === 0) {
    return { revealed: [], remaining: [] };
  }

  const revealed: CardInstance[] = [];
  let i = 0;

  while (i < library.length) {
    const instance = library[i]!;
    revealed.push(instance);
    i++;
    // Stop when we've found a non-token
    if (instance.card.layout !== 'token') {
      break;
    }
  }

  const remaining = library.slice(i);
  return { revealed, remaining };
}

// ── Dice / coin ───────────────────────────────────────────────────────────────

/**
 * Flip a coin.
 * @returns { label: 'Coin', value: 'Heads' | 'Tails' }
 */
export function flipCoin(): { label: 'Coin'; value: 'Heads' | 'Tails' } {
  return {
    label: 'Coin',
    value: Math.random() < 0.5 ? 'Heads' : 'Tails',
  };
}

/**
 * Roll a D6.
 * @returns { label: 'D6', value: string }
 */
export function rollD6(): { label: 'D6'; value: string } {
  return {
    label: 'D6',
    value: String(Math.floor(Math.random() * 6) + 1),
  };
}

/**
 * Roll 2D6.
 * @returns { label: '2×D6', value: string }
 */
export function roll2D6(): { label: '2×D6'; value: string } {
  const a = Math.floor(Math.random() * 6) + 1;
  const b = Math.floor(Math.random() * 6) + 1;
  return {
    label: '2×D6',
    value: String(a + b),
  };
}

/**
 * Roll a D20.
 * @returns { label: 'D20', value: string }
 */
export function rollD20(): { label: 'D20'; value: string } {
  return {
    label: 'D20',
    value: String(Math.floor(Math.random() * 20) + 1),
  };
}

/**
 * Border radius for a full card image, matching a real Magic card's corners
 * (3mm radius on a 63×88mm card = 4.76% of the width).
 *
 * Scryfall's JPEGs are rectangles with the card's rounded corners painted in,
 * and JPEG has no transparency — so the leftover corner wedges are WHITE.
 * Without clipping to the card's own radius those wedges show as white specks
 * on every dark-bordered card. Expressed as percentages (horizontal / vertical)
 * so one value is correct at any rendered size, from a 64px pile to the
 * oversized hover preview, with no coupling to zoom.
 */
export const CARD_CORNER_RADIUS = '4.76% / 3.41%';
